/**
 * Tenant Management Page
 * Super-admin interface for managing tenants
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  Grid,
  FormControlLabel,
  Switch,
  Tabs,
  Tab,
  Divider,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import SortableHeaderCell from '../../components/common/SortableHeaderCell';
import useTableSort from '../../hooks/useTableSort';
import { getStatusColor } from '../../utils/statusColors';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import SettingsIcon from '@mui/icons-material/Settings';
import RefreshIcon from '@mui/icons-material/Refresh';
import StorageIcon from '@mui/icons-material/Storage';
import PaletteIcon from '@mui/icons-material/Palette';
import ExtensionIcon from '@mui/icons-material/Extension';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import CloudIcon from '@mui/icons-material/Cloud';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import LanguageIcon from '@mui/icons-material/Language';
import { apiGet, apiPost, apiPut, apiDelete } from '../../utils/apiClient';
import logger from '../../utils/logger';
import useAsyncData from '../../hooks/useAsyncData';
import { formatDate } from '../../utils/formatters';
import { useTenant } from '../../contexts/TenantContext';
import { useNotification } from '../../contexts/NotificationContext';
import { lightenColor, darkenColor } from '../../utils/themeGenerator';
import ModuleTreeConfig from '../../components/admin/ModuleTreeConfig';
import ConfirmDialog from '../../components/common/ConfirmDialog';

const TENANT_COLUMNS = [
  { key: 'code',           label: 'Code',     align: 'left',   getValue: (t) => t.code },
  { key: 'name',           label: 'Name',     align: 'left',   getValue: (t) => t.name },
  { key: 'status',         label: 'Status',   align: 'left',   getValue: (t) => t.status },
  { key: 'database',       label: 'Database', align: 'left',   sortable: false },
  { key: 'user_count',     label: 'Users',    align: 'center', getValue: (t) => t.user_count || 0 },
  { key: 'enabled_modules',label: 'Modules',  align: 'center', getValue: (t) => t.enabled_modules || 0 },
  { key: 'created_at',     label: 'Created',  align: 'left',   getValue: (t) => t.created_at },
];

const TenantManagement = () => {
  const notify = useNotification();
  const sort = useTableSort();

  // Dialog states
  const [createDialog, setCreateDialog] = useState(false);
const [configDialog, setConfigDialog] = useState({ open: false, tenant: null, tab: 0 });
  const [provisionDialog, setProvisionDialog] = useState({ open: false, tenant: null });
  const [actionLoading, setActionLoading] = useState(false);
  const [actionMenu, setActionMenu] = useState({ anchorEl: null, tenant: null });
  const [provisionStatus, setProvisionStatus] = useState({});

  // Form data
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    description: '',
    primaryDomain: '',
  });

  const { data: tenants, loading, reload: loadTenants } = useAsyncData(
    async () => {
      try {
        return await apiGet('/tenants');
      } catch (err) {
        if (err.response?.status === 403) {
          throw new Error('You do not have permission to manage tenants. Super-admin access required.');
        }
        throw new Error('Failed to load tenants: ' + (err.message || 'Unknown error'));
      }
    },
    { defaultValue: [], onError: notify.error }
  );

  // Load provision status for all tenants when tenants list changes
  useEffect(() => {
    tenants.forEach((tenant) => {
      if (!provisionStatus[tenant.id]) {
        apiGet(`/tenants/${tenant.id}/provision/status`)
          .then((response) => {
            setProvisionStatus((prev) => ({
              ...prev,
              [tenant.id]: response.data,
            }));
          })
          .catch(() => {
            // Silently ignore errors for provision status
          });
      }
    });
  }, [tenants]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCreateTenant = async () => {
    try {
      setActionLoading(true);

      const payload = {
        code: formData.code,
        name: formData.name,
        description: formData.description,
        domains: formData.primaryDomain ? [
          { domain: formData.primaryDomain, type: 'email', isPrimary: true }
        ] : [],
      };

      await apiPost('/tenants', payload);
      notify.success('Tenant created successfully');
      setCreateDialog(false);
      setFormData({ code: '', name: '', description: '', primaryDomain: '' });
      loadTenants();
    } catch (err) {
      notify.error('Failed to create tenant: ' + (err.response?.data?.message || err.message));
    } finally {
      setActionLoading(false);
    }
  };

  const openConfigDialog = async (tenant, tab = 0) => {
    try {
      setActionLoading(true);
      const response = await apiGet(`/tenants/${tenant.id}`);
      setConfigDialog({ open: true, tenant: response.data, tab });
    } catch (err) {
      notify.error('Failed to load tenant details');
    } finally {
      setActionLoading(false);
    }
  };


  // Load provisioning status for a tenant
  const loadProvisionStatus = async (tenantId) => {
    try {
      const response = await apiGet(`/tenants/${tenantId}/provision/status`);
      setProvisionStatus(prev => ({
        ...prev,
        [tenantId]: response.data
      }));
      return response.data;
    } catch (err) {
      logger.error('Failed to load provision status', { message: err.message, status: err.response?.status });
      return null;
    }
  };

  // Provision a new tenant database
  const handleProvision = async (tenant, options = {}) => {
    try {
      setActionLoading(true);

      const response = await apiPost(`/tenants/${tenant.id}/provision`, {
        adminEmail: options.adminEmail,
        adminName: options.adminName,
        seedData: options.seedData !== false,
      });

      notify.success(`Database provisioned successfully: ${response.data?.dbName}`);
      setProvisionDialog({ open: false, tenant: null });

      // Reload status
      await loadProvisionStatus(tenant.id);
      loadTenants();
    } catch (err) {
      notify.error('Failed to provision database: ' + (err.response?.data?.message || err.message));
    } finally {
      setActionLoading(false);
    }
  };

  // Test database connection
  const handleTestConnection = async (tenantId) => {
    try {
      setActionLoading(true);
      const response = await apiPost(`/tenants/${tenantId}/provision/test`);

      if (response.success) {
        notify.success('Database connection successful!');
      } else {
        notify.error('Connection failed: ' + response.data?.error);
      }
    } catch (err) {
      notify.error('Connection test failed: ' + (err.response?.data?.message || err.message));
    } finally {
      setActionLoading(false);
    }
  };

  // Open provision dialog
  const openProvisionDialog = async (tenant) => {
    const status = await loadProvisionStatus(tenant.id);
    setProvisionDialog({ open: true, tenant, status });
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Tenant Management
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={loadTenants}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateDialog(true)}
          >
            Add Tenant
          </Button>
        </Box>
      </Box>
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                {TENANT_COLUMNS.map((col) => (
                  col.sortable === false ? (
                    <TableCell key={col.key} align={col.align}>{col.label}</TableCell>
                  ) : (
                    <SortableHeaderCell key={col.key} sort={sort} columnKey={col.key} align={col.align}>
                      {col.label}
                    </SortableHeaderCell>
                  )
                ))}
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {tenants.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Typography color="text.secondary" sx={{ py: 4 }}>
                      No tenants configured
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                sort.sortRows(tenants, TENANT_COLUMNS).map((tenant) => (
                  <TableRow key={tenant.id} hover>
                    <TableCell>
                      <Typography fontWeight="medium">{tenant.code}</Typography>
                    </TableCell>
                    <TableCell>{tenant.name}</TableCell>
                    <TableCell>
                      <Chip
                        label={tenant.status}
                        size="small"
                        color={getStatusColor(tenant.status)}
                      />
                    </TableCell>
                    <TableCell>
                      {provisionStatus[tenant.id]?.provisioned ? (
                        <Chip
                          icon={<CheckCircleIcon />}
                          label="Provisioned"
                          size="small"
                          color="success"
                          variant="outlined"
                        />
                      ) : process.env.NODE_ENV === 'production' ? (
                        <Chip
                          label="Not Provisioned"
                          size="small"
                          color="warning"
                          variant="outlined"
                        />
                      ) : (
                        <Button
                          size="small"
                          variant="outlined"
                          color="primary"
                          startIcon={<PlayArrowIcon />}
                          onClick={() => openProvisionDialog(tenant)}
                        >
                          Provision
                        </Button>
                      )}
                    </TableCell>
                    <TableCell align="center">{tenant.user_count || 0}</TableCell>
                    <TableCell align="center">{tenant.enabled_modules || 0}</TableCell>
                    <TableCell>{formatDate(tenant.created_at)}</TableCell>
                    <TableCell align="right">
                      <IconButton size="small" onClick={(e) => setActionMenu({ anchorEl: e.currentTarget, tenant })}>
                        <MoreVertIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
      {/* Actions Menu */}
      <Menu
        anchorEl={actionMenu.anchorEl}
        open={Boolean(actionMenu.anchorEl)}
        onClose={() => setActionMenu({ anchorEl: null, tenant: null })}
      >
        <MenuItem onClick={() => { openConfigDialog(actionMenu.tenant, 0); setActionMenu({ anchorEl: null, tenant: null }); }}>
          <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
          <ListItemText>General</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => { openConfigDialog(actionMenu.tenant, 1); setActionMenu({ anchorEl: null, tenant: null }); }}>
          <ListItemIcon><StorageIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Database Config</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => { openConfigDialog(actionMenu.tenant, 2); setActionMenu({ anchorEl: null, tenant: null }); }}>
          <ListItemIcon><PaletteIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Theme</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => { openConfigDialog(actionMenu.tenant, 3); setActionMenu({ anchorEl: null, tenant: null }); }}>
          <ListItemIcon><ExtensionIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Modules</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => { openConfigDialog(actionMenu.tenant, 4); setActionMenu({ anchorEl: null, tenant: null }); }}>
          <ListItemIcon><SettingsIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Settings</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => { openConfigDialog(actionMenu.tenant, 5); setActionMenu({ anchorEl: null, tenant: null }); }}>
          <ListItemIcon><CloudIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Blob Storage</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => { openConfigDialog(actionMenu.tenant, 6); setActionMenu({ anchorEl: null, tenant: null }); }}>
          <ListItemIcon><LanguageIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Domains</ListItemText>
        </MenuItem>
      </Menu>
      {/* Create Tenant Dialog */}
      <Dialog open={createDialog} onClose={() => setCreateDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create New Tenant</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Tenant Code"
              value={formData.code}
              onChange={(e) => setFormData({ ...formData, code: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '') })}
              helperText="Lowercase letters, numbers, and underscores only"
              required
            />
            <TextField
              label="Tenant Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
            />
            <TextField
              label="Description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              multiline
              rows={2}
            />
            <TextField
              label="Primary Email Domain"
              value={formData.primaryDomain}
              onChange={(e) => setFormData({ ...formData, primaryDomain: e.target.value })}
              placeholder="example.com"
              helperText="Users with this email domain will be associated with this tenant"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCreateTenant}
            disabled={actionLoading || !formData.code || !formData.name}
          >
            {actionLoading ? <CircularProgress size={20} /> : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
      {/* Configuration Dialog */}
      <TenantConfigDialog
        open={configDialog.open}
        tenant={configDialog.tenant}
        initialTab={configDialog.tab}
        onClose={() => setConfigDialog({ open: false, tenant: null, tab: 0 })}
        onSuccess={(msg) => {
          notify.success(msg);
          loadTenants();
        }}
        onError={(msg) => notify.error(msg)}
      />
      {/* Provision Database Dialog */}
      <Dialog
        open={provisionDialog.open}
        onClose={() => setProvisionDialog({ open: false, tenant: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Provision Database for {provisionDialog.tenant?.name}
        </DialogTitle>
        <DialogContent>
          {provisionDialog.status?.provisioned ? (
            <Box sx={{ py: 2 }}>
              <Alert severity="success" sx={{ mb: 2 }}>
                Database is already provisioned
              </Alert>
              <Typography variant="body2" color="text.secondary">
                <strong>Database:</strong> {provisionDialog.status?.dbName}<br />
                <strong>Host:</strong> {provisionDialog.status?.dbHost}<br />
                <strong>Status:</strong> {provisionDialog.status?.connectionStatus}
              </Typography>
              <Box sx={{ mt: 2 }}>
                <Button
                  variant="outlined"
                  onClick={() => handleTestConnection(provisionDialog.tenant?.id)}
                  disabled={actionLoading}
                >
                  {actionLoading ? <CircularProgress size={20} /> : 'Test Connection'}
                </Button>
              </Box>
            </Box>
          ) : (
            <Box sx={{ py: 2 }}>
              <Alert severity="info" sx={{ mb: 3 }}>
                This will create a new PostgreSQL database and set up all tables, stored procedures, and reference data for this tenant.
              </Alert>

              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                <strong>Database name:</strong> capital_management_{provisionDialog.tenant?.code}
              </Typography>

              <Alert severity="warning" sx={{ mb: 2 }}>
                <strong>Note:</strong> Database deletion is not available through this interface.
                Once created, databases must be manually removed by a database administrator if needed.
              </Alert>

              <Typography variant="body2" sx={{ mt: 2 }}>
                The provisioning process will:
              </Typography>
              <ul style={{ marginTop: 8, paddingLeft: 20 }}>
                <li>Create a new PostgreSQL database</li>
                <li>Run all schema migrations</li>
                <li>Deploy stored procedures</li>
                <li>Seed reference data (countries, frequencies, etc.)</li>
                <li>Create an admin user with your email</li>
              </ul>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setProvisionDialog({ open: false, tenant: null })}>
            Cancel
          </Button>
          {!provisionDialog.status?.provisioned && (
            <Button
              variant="contained"
              color="primary"
              onClick={() => handleProvision(provisionDialog.tenant)}
              disabled={actionLoading}
              startIcon={actionLoading ? <CircularProgress size={16} /> : <PlayArrowIcon />}
            >
              {actionLoading ? 'Provisioning...' : 'Provision Database'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

/**
 * Tenant Configuration Dialog
 */
const TenantConfigDialog = ({ open, tenant, initialTab, onClose, onSuccess, onError }) => {
  const { tenant: currentTenant, reload: reloadTenant } = useTenant();
  const [tab, setTab] = useState(initialTab || 0);
  const [loading, setLoading] = useState(false);
  const [uploadMsg, setUploadMsg] = useState(null);
  const [uploadError, setUploadError] = useState(null);

  // General (edit) config
  const [generalConfig, setGeneralConfig] = useState({
    name: '',
    description: '',
    status: 'active',
  });

  // Database config
  const [dbConfig, setDbConfig] = useState({
    dbHost: '',
    dbPort: '5432',
    dbName: '',
    dbUser: '',
    dbPassword: '',
    dbSsl: false,
    poolMin: '2',
    poolMax: '10',
  });

  // Theme config (primary, secondary, sidebar colors)
  const [themeConfig, setThemeConfig] = useState({
    themeName: '',
    primaryColor: '#1976d2',
    secondaryColor: '#dc004e',
    sidebarColor: '#1e293b',
    appbarColor: '#0f172a',
    logoUrl: '',
    logoDarkUrl: '',
  });

  // Logo delete confirmation
  const [logoDeleteConfirm, setLogoDeleteConfirm] = useState({ open: false, variant: null });
  // Drag-over highlight per logo variant ('light' | 'dark' | null).
  const [logoDragVariant, setLogoDragVariant] = useState(null);

  const LOGO_ACCEPT_RE = /\.(png|jpe?g|svg|webp)$/i;

  // Shared logo upload (used by both click-to-choose and drag-drop). Auto-uploads
  // on select, matching the existing behaviour, then updates the preview.
  const uploadLogo = async (variant, file) => {
    if (!file) return;
    if (!LOGO_ACCEPT_RE.test(file.name)) {
      setUploadError('Logo must be a PNG, JPEG, SVG or WebP image');
      return;
    }
    const formData = new FormData();
    formData.append('logo', file);
    formData.append('variant', variant);
    try {
      const res = await apiPost(`/tenants/${tenant?.id}/logo`, formData);
      setThemeConfig(prev => (variant === 'light'
        ? { ...prev, logoUrl: res.url }
        : { ...prev, logoDarkUrl: res.url }));
      setUploadMsg(`${variant === 'light' ? 'Light' : 'Dark'} logo uploaded`);
    } catch (err) {
      setUploadError(err.message || 'Upload failed');
    }
  };

  // Drag-drop handlers for a logo box (variant-scoped highlight).
  const logoDropHandlers = (variant) => ({
    onDragOver: (e) => { e.preventDefault(); e.stopPropagation(); if (logoDragVariant !== variant) setLogoDragVariant(variant); },
    onDragEnter: (e) => { e.preventDefault(); e.stopPropagation(); setLogoDragVariant(variant); },
    onDragLeave: (e) => { e.preventDefault(); e.stopPropagation(); setLogoDragVariant(null); },
    onDrop: (e) => { e.preventDefault(); e.stopPropagation(); setLogoDragVariant(null); const f = e.dataTransfer?.files?.[0]; if (f) uploadLogo(variant, f); },
  });

  const handleDeleteLogo = async () => {
    const variant = logoDeleteConfirm.variant;
    setLogoDeleteConfirm({ open: false, variant: null });
    try {
      await apiDelete(`/tenants/${tenant?.id}/logo/${variant}`);
      if (variant === 'light') {
        setThemeConfig(prev => ({ ...prev, logoUrl: '' }));
      } else {
        setThemeConfig(prev => ({ ...prev, logoDarkUrl: '' }));
      }
      setUploadMsg(`${variant === 'light' ? 'Light' : 'Dark'} logo removed`);
    } catch (err) {
      setUploadError(err.message || 'Delete failed');
    }
  };

  // Modules config
  const [modules, setModules] = useState({});

  // Settings config
  const [settingsConfig, setSettingsConfig] = useState({
    systemName: 'Capital Management System',
  });

  // Blob Storage config
  const [blobConfig, setBlobConfig] = useState({
    connectionString: '',
    containerName: 'uploads',
    enabled: false,
  });
  const [blobTesting, setBlobTesting] = useState(false);

  // Domains: tenant config rows used for tenant resolution / branded login pages.
  // A 'custom' domain (e.g. registry.africanalliance.sz) drives the branded login page;
  // 'email' associates user email domains; 'subdomain' for <code>.host patterns.
  const [domains, setDomains] = useState([]);
  const [domainsLoading, setDomainsLoading] = useState(false);
  const [newDomain, setNewDomain] = useState({ domain: '', type: 'custom', isPrimary: false });

  // Blob settings are stored on the tenant's database-config row, so an ACTIVE
  // tenant_database_config must exist before blob storage can be saved. fn_tenant_get_by_id
  // returns databaseConfig (with is_active) only when a row exists; gate on is_active so an
  // inactive row is treated the same as none (mirrors the backend, which updates only active rows).
  const dbConfigured = Boolean(tenant?.databaseConfig?.is_active);

  useEffect(() => {
    setTab(initialTab || 0);
    if (tenant) {
      // Load general config
      setGeneralConfig({
        name: tenant.name || '',
        description: tenant.description || '',
        status: tenant.status || 'active',
      });

      // Load database config
      if (tenant.databaseConfig) {
        setDbConfig({
          dbHost: tenant.databaseConfig.db_host || '',
          dbPort: String(tenant.databaseConfig.db_port || 5432),
          dbName: tenant.databaseConfig.db_name || '',
          dbUser: tenant.databaseConfig.db_user || '',
          dbPassword: '',
          dbSsl: tenant.databaseConfig.db_ssl || false,
          poolMin: String(tenant.databaseConfig.pool_min || 2),
          poolMax: String(tenant.databaseConfig.pool_max || 10),
        });
      }

      // Load theme
      setThemeConfig({
        themeName: tenant.theme_name || tenant.code,
        primaryColor: tenant.primary_color || '#1976d2',
        secondaryColor: tenant.secondary_color || '#dc004e',
        sidebarColor: tenant.background_color || '#1e293b',
        appbarColor: tenant.appbar_color || '#0f172a',
        logoUrl: tenant.logo_url || '',
        logoDarkUrl: tenant.logo_dark_url || '',
      });

      // Load modules - merge is_enabled with config feature flags
      const featureState = {};
      (tenant.modules || []).forEach(m => {
        // Top-level module enabled state
        featureState[m.module_code] = m.is_enabled;
        // Restore per-feature config if saved previously
        if (m.config && typeof m.config === 'object') {
          Object.entries(m.config).forEach(([key, value]) => {
            featureState[`${m.module_code}.${key}`] = value;
          });
        }
      });
      setModules(featureState);

      // Load settings
      setSettingsConfig({
        systemName: tenant.systemName || tenant.settings?.system_name || 'Capital Management System',
      });

      // Load blob storage config
      if (tenant.blobStorage) {
        setBlobConfig({
          connectionString: '',
          containerName: tenant.blobStorage.containerName || 'uploads',
          enabled: tenant.blobStorage.enabled || false,
        });
      } else {
        // Load asynchronously if not in tenant object
        apiGet(`/tenants/${tenant.id}/blob-storage`).then(response => {
          if (response.success && response.data) {
            setBlobConfig({
              connectionString: '',
              containerName: response.data.containerName || 'uploads',
              enabled: response.data.enabled || false,
            });
          }
        }).catch(() => {});
      }
    }
  }, [tenant, initialTab]);

  const loadDomains = async () => {
    if (!tenant?.id) return;
    try {
      setDomainsLoading(true);
      const response = await apiGet(`/tenants/${tenant.id}/domains`);
      if (response.success) {
        setDomains(response.data || []);
      }
    } catch (err) {
      onError('Failed to load domains: ' + (err.message || 'Unknown error'));
    } finally {
      setDomainsLoading(false);
    }
  };

  // Load domains when the Domains tab is opened (tab index 6)
  useEffect(() => {
    if (open && tab === 6 && tenant?.id) {
      loadDomains();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, tab, tenant?.id]);

  const handleAddDomain = async () => {
    const domain = (newDomain.domain || '').trim().toLowerCase();
    if (!domain) {
      onError('Domain is required');
      return;
    }
    try {
      setDomainsLoading(true);
      await apiPost(`/tenants/${tenant.id}/domains`, {
        domain,
        type: newDomain.type,
        isPrimary: newDomain.isPrimary,
      });
      onSuccess('Domain added');
      setNewDomain({ domain: '', type: 'custom', isPrimary: false });
      await loadDomains();
    } catch (err) {
      onError('Failed to add domain: ' + (err.response?.data?.error?.message || err.message || 'Unknown error'));
    } finally {
      setDomainsLoading(false);
    }
  };

  const handleDeleteDomain = async (domainId) => {
    try {
      setDomainsLoading(true);
      await apiDelete(`/tenants/${tenant.id}/domains/${domainId}`);
      onSuccess('Domain removed');
      await loadDomains();
    } catch (err) {
      onError('Failed to remove domain: ' + (err.message || 'Unknown error'));
    } finally {
      setDomainsLoading(false);
    }
  };

  const handleSaveGeneral = async () => {
    try {
      setLoading(true);
      await apiPut(`/tenants/${tenant.id}`, generalConfig);
      onSuccess('Tenant updated successfully');
      onClose();
    } catch (err) {
      onError('Failed to update tenant: ' + (err.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  const handleSaveDatabase = async () => {
    try {
      setLoading(true);
      await apiPut(`/tenants/${tenant.id}/database`, dbConfig);
      onSuccess('Database configuration saved successfully');
      onClose();
    } catch (err) {
      onError('Failed to save database configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveTheme = async () => {
    try {
      setLoading(true);
      await apiPut(`/tenants/${tenant.id}/theme`, themeConfig);
      onSuccess('Theme saved successfully');
      onClose();
    } catch (err) {
      onError('Failed to save theme');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveModules = async () => {
    try {
      setLoading(true);
      // Build payload: extract top-level module enabled + nested feature config
      const modulePayload = {};
      // First pass: collect all paths into modulePayload
      Object.entries(modules).forEach(([path, value]) => {
        const parts = path.split('.');
        const moduleCode = parts[0];
        if (!modulePayload[moduleCode]) {
          modulePayload[moduleCode] = { config: {} };
        }
        if (parts.length > 1) {
          const featureKey = parts.slice(1).join('.');
          modulePayload[moduleCode].config[featureKey] = value;
        }
      });
      // Second pass: derive enabled from whether ANY path under the module is true
      for (const [moduleCode, payload] of Object.entries(modulePayload)) {
        const hasAnyEnabled = modules[moduleCode] === true ||
          Object.values(payload.config).some(v => v === true);
        payload.enabled = hasAnyEnabled;
      }
      await apiPut(`/tenants/${tenant.id}/modules`, { modules: modulePayload });
      onSuccess('Modules saved successfully');
      // Refresh tenant context if we're editing the current tenant
      if (currentTenant?.id === tenant.id) {
        reloadTenant();
      }
      onClose();
    } catch (err) {
      onError('Failed to save modules');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    try {
      setLoading(true);
      await apiPut(`/tenants/${tenant.id}/settings`, {
        systemName: settingsConfig.systemName,
      });
      onSuccess('Settings saved successfully');
      // Refresh tenant context if we're editing the current tenant's settings
      if (currentTenant?.id === tenant.id) {
        reloadTenant();
      }
      onClose();
    } catch (err) {
      onError('Failed to save settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveBlobStorage = async () => {
    try {
      setLoading(true);
      await apiPut(`/tenants/${tenant.id}/blob-storage`, blobConfig);
      onSuccess('Blob storage configuration saved successfully');
      onClose();
    } catch (err) {
      onError('Failed to save blob storage configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleTestBlobConnection = async () => {
    try {
      setBlobTesting(true);
      const response = await apiPost(`/tenants/${tenant.id}/blob-storage/test`);
      if (response.success) {
        onSuccess(`Blob storage connection successful! Container exists: ${response.data.containerExists}`);
      } else {
        onError('Blob storage connection test failed');
      }
    } catch (err) {
      onError(`Blob storage test failed: ${err.response?.data?.message || err.message}`);
    } finally {
      setBlobTesting(false);
    }
  };


  if (!tenant) return null;

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>Configure: {tenant.name}</DialogTitle>
        <DialogContent>
          <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ mb: 2 }} variant="scrollable" scrollButtons="auto">
            <Tab icon={<EditIcon />} label="General" />
            <Tab icon={<StorageIcon />} label="Database" />
            <Tab icon={<PaletteIcon />} label="Theme" />
            <Tab icon={<ExtensionIcon />} label="Modules" />
            <Tab icon={<SettingsIcon />} label="Settings" />
            <Tab icon={<CloudIcon />} label="Blob Storage" />
            <Tab icon={<LanguageIcon />} label="Domains" />
          </Tabs>

          {tab === 0 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Edit tenant details and status.
              </Typography>
              <TextField
                fullWidth
                label="Tenant Name"
                value={generalConfig.name}
                onChange={(e) => setGeneralConfig({ ...generalConfig, name: e.target.value })}
                required
              />
              <TextField
                fullWidth
                label="Description"
                value={generalConfig.description}
                onChange={(e) => setGeneralConfig({ ...generalConfig, description: e.target.value })}
                multiline
                rows={2}
              />
              <TextField
                fullWidth
                select
                label="Status"
                value={generalConfig.status}
                onChange={(e) => setGeneralConfig({ ...generalConfig, status: e.target.value })}
                SelectProps={{ native: true }}
              >
                <option value="active">Active</option>
                <option value="suspended">Suspended</option>
                <option value="inactive">Inactive</option>
              </TextField>
              <Button variant="contained" onClick={handleSaveGeneral} disabled={loading}>
                {loading ? <CircularProgress size={20} /> : 'Save'}
              </Button>
            </Box>
          )}

          {tab === 1 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Configure the database connection for this tenant. Each tenant can have its own isolated database.
              </Typography>
              <Grid container spacing={2}>
                <Grid size={8}>
                  <TextField
                    fullWidth
                    label="Host"
                    value={dbConfig.dbHost}
                    onChange={(e) => setDbConfig({ ...dbConfig, dbHost: e.target.value })}
                  />
                </Grid>
                <Grid size={4}>
                  <TextField
                    fullWidth
                    label="Port"
                    value={dbConfig.dbPort}
                    onChange={(e) => setDbConfig({ ...dbConfig, dbPort: e.target.value })}
                  />
                </Grid>
                <Grid size={12}>
                  <TextField
                    fullWidth
                    label="Database Name"
                    value={dbConfig.dbName}
                    onChange={(e) => setDbConfig({ ...dbConfig, dbName: e.target.value })}
                  />
                </Grid>
                <Grid size={6}>
                  <TextField
                    fullWidth
                    label="Username"
                    value={dbConfig.dbUser}
                    onChange={(e) => setDbConfig({ ...dbConfig, dbUser: e.target.value })}
                  />
                </Grid>
                <Grid size={6}>
                  <TextField
                    fullWidth
                    label="Password"
                    type="password"
                    value={dbConfig.dbPassword}
                    onChange={(e) => setDbConfig({ ...dbConfig, dbPassword: e.target.value })}
                    placeholder="Leave blank to keep existing"
                  />
                </Grid>
                <Grid size={4}>
                  <TextField
                    fullWidth
                    label="Min Pool Size"
                    value={dbConfig.poolMin}
                    onChange={(e) => setDbConfig({ ...dbConfig, poolMin: e.target.value })}
                  />
                </Grid>
                <Grid size={4}>
                  <TextField
                    fullWidth
                    label="Max Pool Size"
                    value={dbConfig.poolMax}
                    onChange={(e) => setDbConfig({ ...dbConfig, poolMax: e.target.value })}
                  />
                </Grid>
                <Grid size={4}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={dbConfig.dbSsl}
                        onChange={(e) => setDbConfig({ ...dbConfig, dbSsl: e.target.checked })}
                      />
                    }
                    label="Use SSL"
                  />
                </Grid>
              </Grid>
              <Button variant="contained" onClick={handleSaveDatabase} disabled={loading}>
                {loading ? <CircularProgress size={20} /> : 'Save Database Config'}
              </Button>
            </Box>
          )}

          {tab === 2 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {uploadMsg && <Alert severity="success" onClose={() => setUploadMsg(null)} sx={{ mb: 1 }}>{uploadMsg}</Alert>}
              {uploadError && <Alert severity="error" onClose={() => setUploadError(null)} sx={{ mb: 1 }}>{uploadError}</Alert>}
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Customize the look and feel for this tenant. Light and dark variants are auto-generated from primary and secondary colors.
              </Typography>
              <TextField
                label="Theme Name"
                value={themeConfig.themeName}
                onChange={(e) => setThemeConfig({ ...themeConfig, themeName: e.target.value })}
              />

              {/* Brand Colors */}
              <Grid container spacing={2}>
                <Grid size={6}>
                  <TextField
                    fullWidth
                    label="Primary Color"
                    type="color"
                    value={themeConfig.primaryColor}
                    onChange={(e) => setThemeConfig({ ...themeConfig, primaryColor: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                    helperText="Main brand color (buttons, links, headers)"
                  />
                </Grid>
                <Grid size={6}>
                  <TextField
                    fullWidth
                    label="Secondary Color"
                    type="color"
                    value={themeConfig.secondaryColor}
                    onChange={(e) => setThemeConfig({ ...themeConfig, secondaryColor: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                    helperText="Accent color (secondary buttons, highlights)"
                  />
                </Grid>
              </Grid>

              {/* Preview of auto-generated variants */}
              <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="caption" color="text.secondary">Primary variants (auto-generated)</Typography>
                  <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
                    <Box sx={{ width: 40, height: 24, borderRadius: 1, backgroundColor: lightenColor(themeConfig.primaryColor, 20) }} title="Light" />
                    <Box sx={{ width: 40, height: 24, borderRadius: 1, backgroundColor: themeConfig.primaryColor }} title="Main" />
                    <Box sx={{ width: 40, height: 24, borderRadius: 1, backgroundColor: darkenColor(themeConfig.primaryColor, 20) }} title="Dark" />
                  </Box>
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="caption" color="text.secondary">Secondary variants (auto-generated)</Typography>
                  <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
                    <Box sx={{ width: 40, height: 24, borderRadius: 1, backgroundColor: lightenColor(themeConfig.secondaryColor, 20) }} title="Light" />
                    <Box sx={{ width: 40, height: 24, borderRadius: 1, backgroundColor: themeConfig.secondaryColor }} title="Main" />
                    <Box sx={{ width: 40, height: 24, borderRadius: 1, backgroundColor: darkenColor(themeConfig.secondaryColor, 20) }} title="Dark" />
                  </Box>
                </Box>
              </Box>

              {/* Sidebar, AppBar & Logos */}
              <Divider sx={{ my: 1 }} />
              <Grid container spacing={2}>
                <Grid size={3}>
                  <TextField
                    fullWidth
                    label="Sidebar Color"
                    type="color"
                    value={themeConfig.sidebarColor}
                    onChange={(e) => setThemeConfig({ ...themeConfig, sidebarColor: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                    helperText="Sidebar background"
                  />
                </Grid>
                <Grid size={3}>
                  <TextField
                    fullWidth
                    label="AppBar Color"
                    type="color"
                    value={themeConfig.appbarColor}
                    onChange={(e) => setThemeConfig({ ...themeConfig, appbarColor: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                    helperText="Header background"
                  />
                </Grid>
                {/* Logo Upload - Light */}
                <Grid size={3}>
                  <Typography variant="caption" color="text.secondary">Light Logo</Typography>
                  <Box {...logoDropHandlers('light')} sx={{ mt: 0.5, p: 1.5, border: '1px dashed', borderColor: logoDragVariant === 'light' ? 'primary.main' : 'divider', bgcolor: logoDragVariant === 'light' ? 'action.hover' : 'transparent', borderRadius: 1, textAlign: 'center', minHeight: 80, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                    {themeConfig.logoUrl ? (
                      <>
                        <Box component="img" src={themeConfig.logoUrl} alt="Light logo" sx={{ maxHeight: 40, maxWidth: '100%', objectFit: 'contain', mb: 1 }} />
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          <Button size="small" component="label" startIcon={<CloudUploadIcon />}>
                            Replace
                            <input type="file" hidden accept="image/png,image/jpeg,image/svg+xml,image/webp" onChange={async (e) => {
                              await uploadLogo('light', e.target.files[0]);
                              e.target.value = '';
                            }} />
                          </Button>
                          <IconButton size="small" color="error" onClick={() => setLogoDeleteConfirm({ open: true, variant: 'light' })}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </>
                    ) : (
                      <Button size="small" component="label" startIcon={<CloudUploadIcon />}>
                        Upload Logo
                        <input type="file" hidden accept="image/png,image/jpeg,image/svg+xml,image/webp" onChange={async (e) => {
                          await uploadLogo('light', e.target.files[0]);
                          e.target.value = '';
                        }} />
                      </Button>
                    )}
                  </Box>
                  <Typography variant="caption" color="text.secondary">PNG, JPEG, SVG, WebP (max 2MB) — or drag and drop</Typography>
                </Grid>
                {/* Logo Upload - Dark */}
                <Grid size={3}>
                  <Typography variant="caption" color="text.secondary">Dark Logo</Typography>
                  <Box {...logoDropHandlers('dark')} sx={{ mt: 0.5, p: 1.5, border: '1px dashed', borderColor: logoDragVariant === 'dark' ? 'primary.main' : 'divider', borderRadius: 1, textAlign: 'center', minHeight: 80, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', backgroundColor: '#1e293b' }}>
                    {themeConfig.logoDarkUrl ? (
                      <>
                        <Box component="img" src={themeConfig.logoDarkUrl} alt="Dark logo" sx={{ maxHeight: 40, maxWidth: '100%', objectFit: 'contain', mb: 1 }} />
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          <Button size="small" component="label" startIcon={<CloudUploadIcon />} sx={{ color: 'grey.300' }}>
                            Replace
                            <input type="file" hidden accept="image/png,image/jpeg,image/svg+xml,image/webp" onChange={async (e) => {
                              await uploadLogo('dark', e.target.files[0]);
                              e.target.value = '';
                            }} />
                          </Button>
                          <IconButton size="small" sx={{ color: 'grey.300' }} onClick={() => setLogoDeleteConfirm({ open: true, variant: 'dark' })}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </>
                    ) : (
                      <Button size="small" component="label" startIcon={<CloudUploadIcon />} sx={{ color: 'grey.300' }}>
                        Upload Logo
                        <input type="file" hidden accept="image/png,image/jpeg,image/svg+xml,image/webp" onChange={async (e) => {
                          await uploadLogo('dark', e.target.files[0]);
                          e.target.value = '';
                        }} />
                      </Button>
                    )}
                  </Box>
                  <Typography variant="caption" color="text.secondary">For dark backgrounds</Typography>
                </Grid>
              </Grid>

              <Button variant="contained" onClick={handleSaveTheme} disabled={loading} sx={{ mt: 2 }}>
                {loading ? <CircularProgress size={20} /> : 'Save Theme'}
              </Button>
            </Box>
          )}

          {tab === 3 && (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Enable or disable features for this tenant.
              </Typography>
              <ModuleTreeConfig
                featureState={modules}
                onChange={setModules}
              />
              <Divider sx={{ my: 1.5 }} />
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button variant="contained" onClick={handleSaveModules} disabled={loading}>
                  {loading ? <CircularProgress size={20} /> : 'Save Modules'}
                </Button>
              </Box>
            </Box>
          )}

          {tab === 4 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Configure tenant settings.
              </Typography>
              <TextField
                fullWidth
                label="System Name"
                value={settingsConfig.systemName}
                onChange={(e) => setSettingsConfig({ ...settingsConfig, systemName: e.target.value })}
                helperText="This name appears in the header bar for all users of this tenant"
              />
              <Button variant="contained" onClick={handleSaveSettings} disabled={loading}>
                {loading ? <CircularProgress size={20} /> : 'Save Settings'}
              </Button>
            </Box>
          )}

          {tab === 5 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Configure Azure Blob Storage for document uploads and file storage.
              </Typography>
              {!dbConfigured && (
                <Alert
                  severity="warning"
                  action={
                    <Button color="inherit" size="small" onClick={() => setTab(1)}>
                      Database tab
                    </Button>
                  }
                >
                  Configure the Database tab first — blob storage settings are stored on the
                  tenant&apos;s database configuration, so the database must be configured (and
                  active) before blob storage can be saved.
                </Alert>
              )}
              <FormControlLabel
                control={
                  <Switch
                    checked={blobConfig.enabled}
                    onChange={(e) => setBlobConfig({ ...blobConfig, enabled: e.target.checked })}
                    disabled={!dbConfigured}
                  />
                }
                label="Enable Blob Storage"
              />
              <Divider />
              <TextField
                fullWidth
                label="Connection String"
                type="password"
                value={blobConfig.connectionString}
                onChange={(e) => setBlobConfig({ ...blobConfig, connectionString: e.target.value })}
                placeholder={blobConfig.enabled ? "Enter Azure Blob Storage connection string" : "Enable blob storage first"}
                disabled={!dbConfigured || !blobConfig.enabled}
                helperText="Azure Blob Storage connection string (stored encrypted)"
              />
              <TextField
                fullWidth
                label="Container Name"
                value={blobConfig.containerName}
                onChange={(e) => setBlobConfig({ ...blobConfig, containerName: e.target.value })}
                disabled={!dbConfigured || !blobConfig.enabled}
                helperText="The blob container name for this tenant's files"
              />
              <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                <Button
                  variant="outlined"
                  onClick={handleTestBlobConnection}
                  disabled={!dbConfigured || !blobConfig.enabled || blobTesting || loading}
                  startIcon={blobTesting ? <CircularProgress size={16} /> : <CheckCircleIcon />}
                >
                  {blobTesting ? 'Testing...' : 'Test Connection'}
                </Button>
                <Button
                  variant="contained"
                  onClick={handleSaveBlobStorage}
                  disabled={!dbConfigured || loading}
                >
                  {loading ? <CircularProgress size={20} /> : 'Save Blob Storage Config'}
                </Button>
              </Box>
            </Box>
          )}

          {tab === 6 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Domains registered for this tenant. A <strong>Custom</strong> domain
                (e.g. <code>registry.africanalliance.sz</code>) drives the branded login page:
                visitors arriving on that hostname see this tenant&apos;s system name, logo and
                theme before signing in. <strong>Email</strong> domains associate users by email;
                <strong> Subdomain</strong> matches the <code>&lt;code&gt;.host</code> pattern.
              </Typography>

              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Domain</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Primary</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {domainsLoading && domains.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} align="center">
                          <CircularProgress size={20} />
                        </TableCell>
                      </TableRow>
                    ) : domains.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} align="center">
                          <Typography variant="body2" color="text.secondary">
                            No domains registered yet.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      domains.map((d) => (
                        <TableRow key={d.id}>
                          <TableCell>{d.domain}</TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={d.domain_type}
                              color={d.domain_type === 'custom' ? 'primary' : 'default'}
                              variant={d.domain_type === 'custom' ? 'filled' : 'outlined'}
                            />
                          </TableCell>
                          <TableCell>{d.is_primary ? 'Yes' : '—'}</TableCell>
                          <TableCell align="right">
                            <Tooltip title="Remove domain">
                              <IconButton
                                size="small"
                                onClick={() => handleDeleteDomain(d.id)}
                                disabled={domainsLoading}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              <Divider />

              <Typography variant="subtitle2">Add a domain</Typography>
              <Grid container spacing={2} alignItems="center">
                <Grid size={5}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Domain"
                    placeholder="registry.africanalliance.sz"
                    value={newDomain.domain}
                    onChange={(e) => setNewDomain({ ...newDomain, domain: e.target.value })}
                  />
                </Grid>
                <Grid size={3}>
                  <TextField
                    fullWidth
                    size="small"
                    select
                    label="Type"
                    value={newDomain.type}
                    onChange={(e) => setNewDomain({ ...newDomain, type: e.target.value })}
                    SelectProps={{ native: true }}
                  >
                    <option value="custom">Custom (branded URL)</option>
                    <option value="email">Email</option>
                    <option value="subdomain">Subdomain</option>
                  </TextField>
                </Grid>
                <Grid size={2}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={newDomain.isPrimary}
                        onChange={(e) => setNewDomain({ ...newDomain, isPrimary: e.target.checked })}
                      />
                    }
                    label="Primary"
                  />
                </Grid>
                <Grid size={2}>
                  <Button
                    fullWidth
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleAddDomain}
                    disabled={domainsLoading || !newDomain.domain.trim()}
                  >
                    Add
                  </Button>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>
      <ConfirmDialog
        open={logoDeleteConfirm.open}
        title="Delete Logo"
        message={`Are you sure you want to remove the ${logoDeleteConfirm.variant === 'dark' ? 'dark' : 'light'} logo?`}
        confirmText="Delete"
        onConfirm={handleDeleteLogo}
        onCancel={() => setLogoDeleteConfirm({ open: false, variant: null })}
      />
    </>
  );
};

export default TenantManagement;
